// Историята на един гост — модалът при клик на ред в справка „Гости".
//
// Редът в таблицата казва „колко"; тук е „кога и как": показателите отгоре
// (посещения, средна сметка, среден престой, общо похарчено, отказани и
// неизпълнени) и резервациите от избрания период като ТАБЛИЦА с колоните и
// реда „Общо" на „Дневник на резервациите" — без филтри, колони, Excel и
// страници (GuestVisitsTable). Редовете СА редовете на „Дневник" за
// телефона на госта (`GET /reports/analytics/reservations/guests/visits`,
// същото право като справката). Средните смята BE по същите правила като
// реда — само по приключилите.
//
// Esc и кликът на фона затварят през antd (`onCancel`) — глобалната
// Esc-навигация разпознава отворения antd слой и не връща назад.

import { useMemo, useState } from "react";
import type { Dayjs } from "dayjs";
import { CloseOutlined, UserOutlined } from "@ant-design/icons";
import { Modal, Spin } from "antd";
import { useTranslation } from "react-i18next";

import { useViewport } from "../../../../hooks/useViewport";
import { useReservationGuestVisits } from "../../../../hooks/useReports";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";
import { fmtEuro } from "../../../../utils/currency";
import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import PhoneLink from "../../../../components/PhoneLink";
import type {
  ReservationGuestRow,
  ReservationGuestVisitsSummary,
  ReservationReportFilters,
} from "../../../../api/reportsReservations";
import { ReportKpiCard } from "../ReportKpiCard";
import { pluralLabel } from "../reportPlural";
import { formatPeriodRange } from "./guestVisitsMath";
import { GuestVisitsTable } from "./GuestVisitsTable";
import { formatReservationMinutes } from "./reservationReportFormat";

// Показателите на госта — общата кутийка на справките в компактния ѝ вид:
// модалът е по-тесен от страницата и 22px число не се събира на телефон.
const GuestKpis = ({
  summary,
  isNarrow,
}: {
  summary: ReservationGuestVisitsSummary;
  isNarrow: boolean;
}) => {
  const { t } = useTranslation();
  const completedSub = t("reportsPage.rvGuestVisitsKpiCompletedSub");
  return (
    <div className={`grid gap-2 ${isNarrow ? "grid-cols-2" : "grid-cols-3"}`}>
      <ReportKpiCard
        compact
        kicker={t("reportsPage.rvGuestVisitsKpiVisits")}
        value={String(summary.visits)}
        sub={pluralLabel(
          t,
          summary.reservations,
          "reportsPage.rvGuestVisitsOfReservationOne",
          "reportsPage.rvGuestVisitsOfReservationsMany",
        )}
      />
      <ReportKpiCard
        compact
        kicker={t("reportsPage.rvGuestVisitsKpiAverageBill")}
        value={
          summary.average_bill === null
            ? EM_DASH
            : fmtEuro(parseDecimal(summary.average_bill))
        }
        sub={completedSub}
      />
      <ReportKpiCard
        compact
        kicker={t("reportsPage.rvGuestVisitsKpiAverageStay")}
        value={formatReservationMinutes(t, summary.average_stay_minutes)}
        sub={completedSub}
      />
      <ReportKpiCard
        compact
        kicker={t("reportsPage.rvGuestVisitsKpiRevenue")}
        value={fmtEuro(parseDecimal(summary.revenue))}
        sub={t("reportsPage.rvGuestVisitsKpiRevenueSub")}
      />
      <ReportKpiCard
        compact
        kicker={t("reportsPage.rvGuestVisitsKpiCancelled")}
        value={String(summary.cancelled)}
        sub={t("reportsPage.rvKpiCancelledSub")}
      />
      <ReportKpiCard
        compact
        kicker={t("reportsPage.rvGuestVisitsKpiNoShow")}
        value={String(summary.expired)}
        sub={t("reportsPage.rvGuestVisitsKpiNoShowSub")}
        hot={summary.expired > 0}
      />
    </div>
  );
};

type Props = {
  /** Кликнатият ред; `null` държи модала затворен. */
  guest: ReservationGuestRow | null;
  /** Периодът на справката — модалът показва същия и го изписва. */
  dateRange: [Dayjs, Dayjs] | null;
  filters: ReservationReportFilters;
  onClose: () => void;
};

export const GuestVisitsModal = ({ guest, dateRange, filters, onClose }: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  // Клас „Document" от рецептата за модали: таблица с 7+ колони не се
  // събира в 640 (като детайла по дни в „Сервитьори").
  const width = isMobile ? undefined : isTablet ? 720 : 880;

  // Последният отворен гост остава на екрана, докато модалът изчезва —
  // иначе при затваряне заглавието и списъкът примигват празни.
  const [lastGuest, setLastGuest] = useState(guest);
  if (guest !== null && guest !== lastGuest) setLastGuest(guest);
  const shownGuest = guest ?? lastGuest;

  // Телефонът е суровият запис от реда — BE го нормализира по същия ключ,
  // по който справката е групирала госта.
  const visitFilters = useMemo(
    () => (shownGuest ? { ...filters, phone: shownGuest.phone } : null),
    [shownGuest, filters],
  );
  const { data, isLoading, isError } = useReservationGuestVisits(visitFilters, guest !== null);

  const periodLabel = dateRange
    ? formatPeriodRange(dateRange[0], dateRange[1])
    : null;
  const visits = data?.rows ?? [];

  return (
    <Modal
      open={guest !== null}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="flex max-h-[82vh] flex-col bg-canvas">
        <header
          className={`flex shrink-0 items-start gap-3 border-b border-shade-2 bg-surface ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-5"
          }`}
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-[10px] bg-brand-tint">
            <UserOutlined className="text-[20px] text-brand-deep-ink" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-[17px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {shownGuest?.guest_name}
            </h2>
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[13px] font-medium leading-[1.4] text-soft">
              {shownGuest ? (
                <PhoneLink
                  phone={shownGuest.phone}
                  withIcon
                  className="font-pos-mono font-semibold text-ink-2"
                />
              ) : null}
              {/* На телефон периодът е на свой ред — иначе пренесеният ред
                  започва с висяща „·". */}
              {periodLabel ? (
                <span className={`whitespace-nowrap ${isNarrow ? "basis-full" : ""}`}>
                  {isNarrow
                    ? t("reportsPage.rvGuestVisitsPeriod", { period: periodLabel })
                    : `· ${t("reportsPage.rvGuestVisitsPeriod", { period: periodLabel })}`}
                </span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="grid size-[30px] shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-shade-1 hover:text-ink"
          >
            <CloseOutlined className="text-[14px]" />
          </button>
        </header>

        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
            isNarrow ? "px-3 py-3" : "px-[18px] py-4"
          }`}
        >
          {isLoading ? (
            <div className="grid place-items-center py-14">
              <Spin size="large" />
            </div>
          ) : isError ? (
            <p className="py-10 text-center text-[13px] font-medium text-danger-ink">
              {t("reportsPage.rvGuestVisitsFailed")}
            </p>
          ) : !data || visits.length === 0 ? (
            <p className="py-10 text-center text-[13px] font-medium text-soft">
              {t("reportsPage.rvGuestVisitsEmpty")}
            </p>
          ) : (
            <div className="flex flex-col gap-3.5">
              <GuestKpis summary={data.summary} isNarrow={isNarrow} />

              <GuestVisitsTable rows={visits} ariaLabel={t("reportsPage.rvGuestVisitsListTitle")} />
            </div>
          )}
        </div>

        <footer
          className={`flex shrink-0 items-center justify-end border-t border-shade-2 bg-surface ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-4"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`h-10 rounded-[10px] border border-shade-3 bg-surface px-4 text-[13px] font-bold text-soft transition-colors hover:bg-fill ${
              isMobile ? "w-full" : ""
            }`}
          >
            {t("common.close")}
          </button>
        </footer>
      </div>
    </Modal>
  );
};
